import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Link,
  Stack,
  Backdrop,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import OrderTotal from "./orderTotal";
import dataService from "@/app/services/dataService";
import {
  SavedBillingPayload,
  SavedDeliveryPickupPayload,
  SavedPaymentPayload,
} from "@/app/models/checkout/types";
import { STORAGE_KEYS } from "@/app/utils/localStorageHelper/checkoutKeys";
import {
  clearFromStorage,
  loadFilesFromStorage,
  loadFromStorage,
} from "@/app/utils/localStorageHelper/checkoutStorage";
import { useGlobalNotification } from "@/app/components/header/GlobalNotificationContext";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";

interface OrderSummaryPanelProps {
  enablePromotions: boolean;
  enablePlaceOrder: boolean;
  contactPhoneNumber?: string;
  contactEmail?: string;
  itemsCount: number;
  discountCode: string;
  reachedPromoCodeMaxDiscount: boolean;
  redeemedRewardPoints: number;
  orderTotal: number;
  maxApplicableRewardPoints: number;
  discount: number;
  quoteEnabled: boolean;
}

const formatPhoneNumber = (phone: string): string => {
  return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
};

const OrderSummaryPanel: React.FC<OrderSummaryPanelProps> = ({
  enablePromotions,
  enablePlaceOrder,
  contactEmail,
  contactPhoneNumber,
  itemsCount,
  discountCode,
  reachedPromoCodeMaxDiscount,
  redeemedRewardPoints,
  orderTotal,
  maxApplicableRewardPoints,
  discount,
  quoteEnabled,
}) => {
  const { setNotification } = useGlobalNotification();
  const [loading, setLoading] = useState(false);
  const [quoteChecked, setQuoteChecked] = useState(false);
  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";

  const handlePlaceOrder = async () => {
    try {
      const cachedBilling = loadFromStorage<SavedBillingPayload>(
        userId,
        STORAGE_KEYS.BILLING
      );
      const cachedPickUpDelivery = loadFromStorage<SavedDeliveryPickupPayload>(
        userId,
        STORAGE_KEYS.PICKUP_DELIVERY
      );
      const cachedPayment = loadFromStorage<SavedPaymentPayload>(
        userId,
        STORAGE_KEYS.PAYMENT
      );
      if (
        cachedBilling == null ||
        cachedPickUpDelivery == null ||
        cachedPayment == null
      )
        return;
      setLoading(true);
      const formData = new FormData();

      // root
      formData.append("EnablePromotions", String(enablePromotions));

      // ----- Billing -----
      formData.append("Billing.JobName", cachedBilling.jobName ?? "");

      const files = loadFilesFromStorage(
        userId,
        STORAGE_KEYS.BILLING_ATTACHMENTS
      );
      files?.forEach((file) => {
        formData.append("Billing.Attachments", file);
      });

      // ----- PickupDelivery -----
      formData.append(
        "PickupDelivery.IsPickup",
        String(cachedPickUpDelivery.isPickup)
      );
      formData.append("PickupDelivery.Note", cachedPickUpDelivery.note ?? "");

      if (cachedPickUpDelivery.selectedDate) {
        formData.append(
          "PickupDelivery.SelectedDate",
          cachedPickUpDelivery.selectedDate
        );
      }

      formData.append(
        "PickupDelivery.SelectedTime",
        cachedPickUpDelivery.selectedTime ?? ""
      );

      if (cachedPickUpDelivery.deliveryAddress) {
        const da = cachedPickUpDelivery.deliveryAddress;

        if (da.shippingAddressId != null) {
          formData.append(
            "PickupDelivery.DeliveryAddress.ShippingAddressId",
            String(da.shippingAddressId)
          );
        }
        formData.append(
          "PickupDelivery.DeliveryAddress.Company",
          da.company ?? ""
        );
        formData.append(
          "PickupDelivery.DeliveryAddress.FirstName",
          da.firstName ?? ""
        );
        formData.append(
          "PickupDelivery.DeliveryAddress.LastName",
          da.lastName ?? ""
        );
        formData.append(
          "PickupDelivery.DeliveryAddress.Address1",
          da.address1 ?? ""
        );
        formData.append(
          "PickupDelivery.DeliveryAddress.Address2",
          da.address2 ?? ""
        );
        formData.append("PickupDelivery.DeliveryAddress.City", da.city ?? "");
        formData.append(
          "PickupDelivery.DeliveryAddress.StateProvinceId",
          da.state != null ? String(da.state) : ""
        );
        formData.append("PickupDelivery.DeliveryAddress.Zip", da.zip ?? "");
        formData.append("PickupDelivery.DeliveryAddress.Phone", da.phone ?? "");
      }

      // ----- Payment -----
      const rewardPoints =
        cachedPayment.selectedRewardPoints === "" ||
        cachedPayment.selectedRewardPoints == null
          ? 0
          : Number(cachedPayment.selectedRewardPoints);

      formData.append("Payment.RewardsPoint", rewardPoints.toString());
      formData.append("Payment.PaymentTerms", cachedPayment.paymentTerms ?? "");
      formData.append(
        "Payment.InterestedOnAccount",
        String(Boolean(cachedPayment.interestedOnAccount))
      );

      const response = await dataService.placeOrder(
        formData,
        quoteEnabled ? quoteChecked : null
      );

      if (response.success === true && response.data.warnings.length == 0) {
        clearFromStorage(userId, STORAGE_KEYS.BILLING);
        clearFromStorage(userId, STORAGE_KEYS.BILLING_ATTACHMENTS);
        clearFromStorage(userId, STORAGE_KEYS.PICKUP_DELIVERY);
        clearFromStorage(userId, STORAGE_KEYS.PAYMENT);
        clearFromStorage(userId, STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL);

        var data = response.data;

        if (data.orderId)
          window.location.href = `/lbpcustomer/checkout/completed/?orderIds=${data.orderId}`;
        else if (data.isAccountOnHoldDueInactivity)
          window.location.href = `/lbpcustomer/checkout/completed/?isAccountOnHoldDueInactivity=true`;
        else if (data.isBCPOrderIntegrationOn)
          window.location.href = `/lbpcustomer/checkout/completed/?isBCPOrderIntegrationOn=true`;
      } else if (response?.data?.warnings?.length) {
        setNotification(response.data.warnings.join(" "), "error");
      } else if (response.error?.length) {
        setNotification(response.error, "error");
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        className="statusCheckoutRight"
        width={"100%"}
        sx={{
          position: "sticky",
          top: 24,
          alignSelf: "flex-start",
        }}
      >
        <Box
          className="grey-container"
          p={3}
          borderRadius={2}
          bgcolor="#f5f5f5"
        >
          <Stack spacing={2}>
            <Button
              onClick={handlePlaceOrder}
              className="filledBlueButton blueButtonOutline"
              fullWidth
              disabled={!enablePlaceOrder}
            >
              Place Order
            </Button>

            <OrderTotal
              itemsCount={itemsCount}
              discountCode={discountCode}
              reachedPromoCodeMaxDiscount={reachedPromoCodeMaxDiscount}
              redeemedRewardPoints={redeemedRewardPoints}
              orderTotal={orderTotal}
              maxApplicableRewardPoints={maxApplicableRewardPoints}
              discount={discount}
            />
            <Button
              onClick={handlePlaceOrder}
              className="filledBlueButton blueButtonOutline"
              fullWidth
              disabled={!enablePlaceOrder}
            >
              Place Order
            </Button>
          </Stack>
        </Box>

        <Box mt={3}></Box>

        <Box mt={2}>
          {quoteEnabled && (
            <FormControlLabel
              control={
                <Checkbox
                  id="createQuoteCheckbox"
                  checked={quoteChecked}
                  onChange={(e) => setQuoteChecked(e.target.checked)}
                />
              }
              label="Create Quote"
            />
          )}
          <Typography variant="body1" fontWeight={600}>
            Need help ?
          </Typography>
          <Typography variant="body2" color={"black"}>
            Read Our:{" "}
            <Link href={process.env.REACT_APP_WEB_URL + "/faq"}>FAQs</Link>
          </Typography>

          {contactPhoneNumber && (
            <Typography variant="body2" color={"black"}>
              Call your local branch :{" "}
              <Link href={`tel:${formatPhoneNumber(contactPhoneNumber)}`}>
                {formatPhoneNumber(contactPhoneNumber)}
              </Link>
            </Typography>
          )}

          {contactEmail && (
            <Typography variant="body2" color={"black"}>
              Email your local Branch :{" "}
              <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>
            </Typography>
          )}
        </Box>
      </Box>
      <Backdrop
        sx={{
          backgroundColor: "rgba(221, 221, 221, 0.9)",
          color: "#000",
          zIndex: "1600 !important",
        }}
        open={loading}
      >
        <Box textAlign="center">
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2, fontWeight: 600, fontSize: "18px" }}>
            Your order is processing... Please do not click back or navigate
            away.
          </Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default OrderSummaryPanel;
